import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertCircle, ArrowLeft, Loader2, Download } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ImportApplications({ onBack, user }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file);
        setResult(null);
      } else {
        toast.error(t('import.supportedFormat'));
      }
    }
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(10);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Simulate progress while uploading
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const response = await api.post('/import/applications', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setResult(response.data);
      
      if (response.data.success) {
        toast.success(`${t('import.importSuccess')}: ${response.data.imported} ${t('import.imported')}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error.response?.data?.detail || t('import.importFailed');
      toast.error(errorMessage);
      setResult({
        success: false,
        error: errorMessage
      });
    } finally {
      setUploading(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setResult(null);
    setUploadProgress(0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="gap-2"
            data-testid="import-back-button"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('application.back')}
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900" data-testid="import-title">
              {t('import.title')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('import.supportedFormat')}
            </p>
          </div>
        </div>
        {/* Download Template Button */}
        <a 
          href="/templates/survey_import_template.xlsx"
          download="survey_import_template.xlsx"
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          data-testid="download-template-btn"
        >
          <Download className="h-4 w-4" />
          {t('import.downloadTemplate') || 'Download Template'}
        </a>
      </div>

      {/* Main Import Card */}
      <Card className="border-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            {t('import.uploadExcel')}
          </CardTitle>
          <CardDescription>
            {t('import.dragDrop')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
              ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}
              ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            data-testid="import-dropzone"
          >
            <input {...getInputProps()} data-testid="import-file-input" />
            <AnimatePresence mode="wait">
              {selectedFile ? (
                <motion.div
                  key="selected"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex flex-col items-center gap-3"
                >
                  <FileSpreadsheet className="h-12 w-12 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex flex-col items-center gap-3"
                >
                  <Upload className={`h-12 w-12 ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-700">
                      {isDragActive ? 'Drop the file here' : t('import.dragDrop')}
                    </p>
                    <p className="text-sm text-gray-500">{t('import.supportedFormat')}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-2"
            >
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('import.importing')}
                </span>
                <span className="font-medium">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            {selectedFile && !uploading && !result && (
              <>
                <Button variant="outline" onClick={resetImport} data-testid="import-reset-button">
                  {t('common.cancel')}
                </Button>
                <Button 
                  onClick={handleUpload} 
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="import-upload-button"
                >
                  <Upload className="h-4 w-4" />
                  {t('import.uploadExcel')}
                </Button>
              </>
            )}
            {result && (
              <Button onClick={resetImport} variant="outline" data-testid="import-new-button">
                Import Another File
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Card */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className={`border-2 ${result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  {result.success ? (
                    <CheckCircle2 className="h-8 w-8 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-8 w-8 text-red-600 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                      {result.success ? t('import.importSuccess') : t('import.importFailed')}
                    </h3>
                    
                    {result.success ? (
                      <div className="mt-4 grid grid-cols-3 gap-4">
                        {/* Imported */}
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle2 className="h-5 w-5" />
                            <span className="font-medium">{t('import.imported')}</span>
                          </div>
                          <p className="text-2xl font-bold text-green-800 mt-2" data-testid="import-result-imported">
                            {result.imported}
                          </p>
                        </div>
                        
                        {/* Skipped */}
                        <div className="bg-white rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center gap-2 text-yellow-700">
                            <AlertCircle className="h-5 w-5" />
                            <span className="font-medium">{t('import.skipped')}</span>
                          </div>
                          <p className="text-2xl font-bold text-yellow-800 mt-2" data-testid="import-result-skipped">
                            {result.skipped}
                          </p>
                        </div>
                        
                        {/* Errors */}
                        <div className="bg-white rounded-lg p-4 border border-red-200">
                          <div className="flex items-center gap-2 text-red-700">
                            <XCircle className="h-5 w-5" />
                            <span className="font-medium">{t('import.errors')}</span>
                          </div>
                          <p className="text-2xl font-bold text-red-800 mt-2" data-testid="import-result-errors">
                            {result.errors}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 text-red-600">{result.error}</p>
                    )}

                    {/* Error Details */}
                    {result.error_details && result.error_details.length > 0 && (
                      <div className="mt-4 bg-white rounded-lg p-4 border border-red-200">
                        <h4 className="font-medium text-red-800 mb-2">{t('import.errorDetails')}</h4>
                        <ul className="text-sm text-red-600 space-y-1">
                          {result.error_details.map((err, idx) => (
                            <li key={idx}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Empty Hobli Warning */}
                    {result.empty_hobli_count > 0 && (
                      <div className="mt-4 bg-amber-100 rounded-lg p-4 border-2 border-amber-400" data-testid="empty-hobli-warning">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-amber-800 text-lg mb-1">
                              ⚠️ {t('import.dataQualityWarning')}
                            </h4>
                            <p className="text-amber-700 font-medium">
                              {t('import.emptyHobliWarning', { count: result.empty_hobli_count })}
                            </p>
                            <p className="text-sm text-amber-600 mt-2">
                              {t('import.emptyHobliTip')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Other Warnings */}
                    {result.warnings && result.warnings.length > 0 && (
                      <div className="mt-4 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          {t('import.warnings')} ({result.warning_count}):
                        </h4>
                        <ul className="text-sm text-yellow-700 space-y-1 max-h-40 overflow-y-auto">
                          {result.warnings.map((warn, idx) => (
                            <li key={idx}>• {warn}</li>
                          ))}
                        </ul>
                        {result.warning_count > result.warnings.length && (
                          <p className="text-xs text-yellow-600 mt-2 italic">
                            {t('import.moreWarnings', { count: result.warning_count - result.warnings.length })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">{t('import.formatInfo')}</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>{t('import.formatTip1')}</li>
                <li>{t('import.formatTip2')}</li>
                <li>{t('import.formatTip3')}</li>
                <li>{t('import.formatTip4')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
